import { useEffect, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import type { FeatureId } from "@/lib/featureAccess";
import { notifyError } from "@/lib/toast";
import { Role } from "@/types/auth";

interface FeatureProtectedRouteProps {
  children: ReactNode;
  feature: FeatureId;
  /** Where to send a user who cannot access `feature`. Defaults to the role's home. */
  fallbackPath?: string;
}

/** Roles whose home screen is "Mes tâches" rather than the dashboard. */
const WORKER_ROLES: ReadonlySet<string> = new Set<string>([
  Role.PREPARATEUR,
  Role.LIVREUR,
  Role.ATELIER,
]);

export default function FeatureProtectedRoute({
  children,
  feature,
  fallbackPath,
}: FeatureProtectedRouteProps) {
  const { role } = useAuth();
  const { allowed, loading } = useFeatureAccess(feature);
  const denied = !loading && !allowed;

  // A deep link the role cannot open used to bounce to "/" without a word;
  // say why before redirecting.
  useEffect(() => {
    if (denied) notifyError("Vous n’avez pas accès à cette page");
  }, [denied]);

  if (loading) return null;
  if (!allowed) {
    const target =
      fallbackPath ?? (role && WORKER_ROLES.has(role) ? "/mes-taches" : "/");
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
